import logger from "@/lib/logger";
import { Journal, JournalType, JSONSafe, TaskType } from "@/types";

export type EventSourceMessage =
  | (Pick<Journal, "id" | "type" | "createdAt" | "message"> & {
      type: JournalType;
      taskType?: TaskType;
      appId?: string;
      hostId?: string;
      appName?: string;
      hostName?: string;
    })
  | {
      type: "invalidate";
      createdAt: Date;
      path: string;
    };

export type EventSourceMessageInvalidate = Extract<EventSourceMessage, { type: "invalidate" }>;
export type EventSourceMessageJournal = Extract<EventSourceMessage, { type: JournalType }>;

export type EventSourceHandle = {
  close: () => void;
};

const ERROR_THRESHOLD = 50;
const BASE_RECONNECT_DELAY_MS = 200;
const MAX_RECONNECT_DELAY_MS = 30_000;

/** FIXME: https://github.com/rexxars/eventsource-client */
export const createEventSource = (
  onError?: (error: Event) => void,
  onMessage?: (message: JSONSafe<EventSourceMessage>) => void
): EventSourceHandle => {
  let destroyed = false;
  let eventSource: EventSource | undefined;
  let reconnectTimeout: ReturnType<typeof setTimeout> | undefined;
  let attempt = 0;

  const connect = () => {
    if (destroyed) {
      return;
    }
    eventSource = new EventSource("/api/stream");

    eventSource.addEventListener("open", () => {
      logger.verbose("Event source opened");
      attempt = 0;
    });

    eventSource.addEventListener("message", event => {
      const msg = JSON.parse(event.data as string) as JSONSafe<EventSourceMessage>;
      logger.verbose("Event source message", msg);
      if (onMessage) {
        onMessage(msg);
      }
    });

    eventSource.addEventListener("error", error => {
      if (destroyed) {
        return;
      }

      const msg = JSON.stringify(error);
      const isServerClose =
        error.target === eventSource &&
        (eventSource.readyState === EventSource.CLOSED ||
          // FIXME: this is what happens to be sent when the server is shutting down
          msg === '{"isTrusted":true}');

      attempt++;
      eventSource?.close();

      if (isServerClose || attempt <= ERROR_THRESHOLD) {
        const delay = Math.min(
          BASE_RECONNECT_DELAY_MS * Math.pow(2, attempt - 1),
          MAX_RECONNECT_DELAY_MS
        );
        logger.warn("Reconnecting to event source in", delay, "ms");
        reconnectTimeout = setTimeout(connect, delay);
      } else {
        if (onError) {
          onError(error);
        }
      }
    });

    eventSource.addEventListener("ping", event => {
      logger.verbose("Event source ping", event);
    });
  };

  connect();

  return {
    close: () => {
      destroyed = true;
      if (reconnectTimeout !== undefined) {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = undefined;
      }
      eventSource?.close();
    }
  };
};
